import { looksLikeCaseReference } from '@concord/domain';
import { Notice, PageHead, Panel, PanelHead } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import type { CaseListResponse } from '../cases/CasesPage';
import { CaseTable } from '../cases/CaseTable';
import { ConsoleLayout } from './ConsoleLayout';

interface Person {
  id: string;
  publicId: number;
  roleLabel: string;
  firstName: string;
  lastName: string;
  email: string;
}

/** The top-bar search: a case reference goes straight to the case (DIR-03); anything else searches cases and people. */
export function SearchPage({ me }: { me: Me }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = (params.get('q') ?? '').trim();
  const isRef = looksLikeCaseReference(q);
  const cases = useQuery({
    queryKey: ['search-cases', q],
    queryFn: () => api<CaseListResponse>(`/staff/cases?q=${encodeURIComponent(q)}&limit=20`),
    enabled: !isRef && q.length > 0,
  });
  const people = useQuery({
    queryKey: ['search-people', q],
    queryFn: () => api<{ rows: Person[] }>(`/staff/directory?q=${encodeURIComponent(q)}`),
    enabled: !isRef && q.length > 0,
  });
  if (isRef) return <Navigate to={`/console/cases/${q.toUpperCase()}`} replace />;
  return (
    <ConsoleLayout me={me} current="">
      <PageHead title={`Results for “${q}”`} />
      {(cases.isError || people.isError) && (
        <Notice tone="bad">{errorMessage(cases.error ?? people.error)}</Notice>
      )}
      <Panel aria-label="Cases">
        <PanelHead title="Cases" sub={cases.data?.total ?? ''} />
        {cases.data?.rows.length ? (
          <CaseTable rows={cases.data.rows} tz={me.timezone} now={new Date()} />
        ) : (
          <div className="pbody sub2">No cases.</div>
        )}
      </Panel>
      <Panel aria-label="People">
        <PanelHead title="People" sub={people.data?.rows.length ?? ''} />
        <div className="tw">
          <table>
            <tbody>
              {people.data?.rows.map((p) => (
                <tr
                  key={p.id}
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/console/directory/${p.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/console/directory/${p.id}`)}
                >
                  <td>{p.roleLabel}</td>
                  <td className="pt">
                    {p.firstName} {p.lastName}
                  </td>
                  <td>{p.email}</td>
                  <td className="mono">{p.publicId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </ConsoleLayout>
  );
}
